import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, ScrollView, TouchableOpacity, Pressable } from "react-native";
import { Button, Card, Text } from "react-native-paper";
import * as DocumentPicker from "expo-document-picker";
import { useFocusEffect } from "@react-navigation/native";
import { View } from "../../components/shared/Themed";
import Spinner from "../../components/shared/Spinner";
import useColorScheme from "../../hooks/useColorScheme";
import { CustomHorizontalLine } from "../../components/custom/CustomHorizontalLine";

import * as XLSX from "xlsx";
import {
  containerMongoDbModal,
  containerSpreadSheetModal,
} from "../../models/containersModel";
import { SelectBoxControl } from "../../components/custom/SelectBoxControl";
import { TextInputControl } from "../../components/custom/TextInputControl";
import { DateInputControl } from "../../components/custom/DateInputControl";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";

import { MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import { addDaysToDate } from "../../utils/dateUtility";

import { useForm, Controller } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import {
  manageContainer,
  getContainerById,
  manageBulkContainers,
} from "../../services/containersService";
import { containersState } from "../../redux/state/containers";
import { ReadOnlyProps } from "../../utils/templates";
import { useSelector } from "react-redux";
import oktaConfig from "../../configs/okta";

interface Props {
  route: any;
  navigation: any;
}

export default function ManageContainers({
  route,
  navigation,
}: ReadOnlyProps<Props>) {
  const params = route.params;
  const { userRoles } = useSelector((state: any) => state.configurations);

  const isContainerUpdated: boolean =
    params.container && Object.keys(params.container).length > 0;
  const containerRecord: containerMongoDbModal = isContainerUpdated
    ? JSON.parse(params.container)
    : {};

  const toast = useToast();
  const colorScheme = useColorScheme();

  const isUserAdmin = userRoles.some((role: string) =>
    containersState.allowedRolesForContainers.includes(role)
  );

  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const scrollRef = React.useRef<ScrollView>(null);

  useFocusEffect(
    React.useCallback(() => {
      // Small delay to ensure layout is ready if needed, 
      // but usually scrollTo({y:0}) on focus is enough
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, [])
  );
  const [displayLoader, setDisplayLoader] = useState<boolean>(false);
  const [displayBulkLoader, setDisplayBulkLoader] = useState<boolean>(false);
  const [activePicker, setActivePicker] = useState<string | null>(null);

  const ExcelDateToJSDate = (date: number) => {
    return new Date(Math.round((date - 25569) * 86400 * 1000));
  };

  const ProcessFile = async () => {
    const selectedFileResult = await DocumentPicker.getDocumentAsync({
      type: "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const containers: Array<any> = [];
    if (
      selectedFileResult.canceled === false &&
      selectedFileResult.assets &&
      selectedFileResult.assets.length > 0
    ) {
      const selectedFile = selectedFileResult.assets[0];
      setDisplayBulkLoader(true);
      const selectedFile_ArrayBuffer = await fetch(selectedFile.uri);
      const workbook = XLSX.read(await selectedFile_ArrayBuffer.arrayBuffer(), {
        type: "buffer",
      });
      const Sheet = workbook.Sheets["Final"];
      const SheetRows =
        XLSX.utils.sheet_to_json<containerSpreadSheetModal>(Sheet);

      SheetRows.forEach((row) => {
        const etaStore = row["ETA(Store)"] as string;
        const eta_store_end_date =
          etaStore.split("-")[1] !== undefined
            ? (etaStore.split("-")[1] as string).split("/")
            : "";
        const eta_store_end_date_format =
          eta_store_end_date !== ""
            ? `${eta_store_end_date[1]}/${eta_store_end_date[0]}/${eta_store_end_date[2]}`
            : "";

        containers.push({
          coo_number: row["Certificate of Import"],
          status: row.Type,
          container_number: row["Container No."],
          deliver_to: row["Ship Via"],
          etd: ExcelDateToJSDate(row.ETD as number),
          eta_port: ExcelDateToJSDate(row["ETA(Port)"] as number),
          eta_store:
            eta_store_end_date_format !== ""
              ? new Date(eta_store_end_date_format)
              : null,
          country_state: row.State,
          priority: row["Priority Level"],
        });
      });
      const result = await manageBulkContainers(containers);
      toast.show(result.data.message, {
        type: result.isSuccessfull ? "success" : "danger",
      });
      if (result.isSuccessfull) {
        backToList();
      }
    } else {
      toast.show("Error: couldn't retrieve the file", {
        type: "danger",
      });
    }

    setDisplayBulkLoader(false);
  };

  const backToList = () => {
    resetForm();
    setTimeout(() => {
      window.location.href = `${oktaConfig.api.redirectUri}/containers/list`;
    }, 1000);
  };

  const containerDefaultValues = {
    importer: "",
    type: "",
    size: "",
    container_number: "",
    deliver_to: "",
    country_state: "",
    etd: new Date(),
    eta_port: addDaysToDate(new Date(), 14),
    eta_store: addDaysToDate(new Date(), 29),
    updated_eta: new Date(),
    dd_number: "",
    coo_number: "",
    status: "",
    remark: "",
    delay_reason: "",
    vessal_detail: "",
    priority: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: containerDefaultValues,
  });

  const resetForm = () => {
    reset(containerDefaultValues);
  };

  useEffect(() => {
    if (isContainerUpdated) {
      setValue(
        "importer",
        containerRecord.importer ? (containerRecord.importer as string) : ""
      );
      setValue(
        "type",
        containerRecord.type ? (containerRecord.type as string) : ""
      );
      setValue(
        "size",
        containerRecord.size ? (containerRecord.size as string) : ""
      );
      setValue(
        "container_number",
        containerRecord.container_number ? containerRecord.container_number : ""
      );
      setValue(
        "deliver_to",
        containerRecord.deliver_to ? containerRecord.deliver_to : ""
      );
      setValue(
        "country_state",
        containerRecord.country_state
          ? (containerRecord.country_state as string)
          : ""
      );
      setValue(
        "etd",
        containerRecord.etd ? new Date(containerRecord.etd) : new Date()
      );
      setValue(
        "eta_port",
        containerRecord.eta_port
          ? new Date(containerRecord.eta_port)
          : addDaysToDate(new Date(), 14)
      );
      setValue(
        "eta_store",
        containerRecord.eta_store
          ? new Date(containerRecord.eta_store)
          : addDaysToDate(new Date(), 29)
      );
      setValue(
        "updated_eta",
        containerRecord.updated_eta
          ? new Date(containerRecord.updated_eta)
          : new Date()
      );
      setValue(
        "dd_number",
        containerRecord.dd_number ? containerRecord.dd_number : ""
      );
      setValue(
        "coo_number",
        containerRecord.coo_number ? containerRecord.coo_number : ""
      );
      setValue(
        "status",
        containerRecord.status ? (containerRecord.status as string) : ""
      );
      setValue("remark", containerRecord.remark ? containerRecord.remark : "");
      setValue(
        "delay_reason",
        containerRecord.delay_reason ? containerRecord.delay_reason : ""
      );
      setValue(
        "vessal_detail",
        containerRecord.vessal_detail ? containerRecord.vessal_detail : ""
      );
      setValue(
        "priority",
        containerRecord.priority ? (containerRecord.priority as string) : ""
      );

      //Set displayLoader to false
      setDisplayLoader(false);
    }
  }, [route.params]);

  const isContainerExists = (containerNumber: string = "") => {
    return getContainerById(containerNumber)
      .then((result) => {
        return Object.keys(result.data).length > 0;
      })
      .catch(() => {
        return false;
      });
  };

  const onSubmit = async (formData: any) => {
    setDisplayLoader(!displayLoader);

    const transactionType = params.mode === "create" ? "add" : "update";
    const containerForm =
      params.mode === "create"
        ? formData
        : {
          _id: containerRecord._id,
          ...formData,
        };

    await manageContainer(containerForm, transactionType)
      .then((result) => {
        toast.show(result.data.message, {
          type: "success",
        });

        backToList();
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      })
      .finally(() => {
        setDisplayLoader(!displayLoader);
      });
  };

  return (
    <ScrollView ref={scrollRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
      <View style={styles.pageHeader}>
        <Text style={styles.pageTitle}>
          {params.mode === "create" ? "Create Container" : "Edit Container"}
        </Text>
        <Text style={styles.pageSubtitle}>
          {params.mode === "create" ? "Add a new container to the system" : "Modify existing container details"}
        </Text>
      </View>

      <Pressable
        onPress={() => setActivePicker(null)}
        style={styles.container}
      >
        <View style={styles.card}>
          <View style={{ width: "100%" }}>
            {params.mode === "create" && (
              <>
                <TouchableOpacity
                  onPress={ProcessFile}
                  disabled={displayBulkLoader}
                  style={{
                    backgroundColor: "#F8FAFC",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: "#E2E8F0",
                    borderRadius: 12,
                    padding: 24,
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 32,
                  }}
                >
                  {displayBulkLoader ? (
                    <Spinner size="small" color="#3B82F6" />
                  ) : (
                    <>
                      <Text style={{ color: "#3B82F6", fontWeight: "700", fontSize: 15 }}>
                        UPLOAD CONTAINERS
                      </Text>
                      <Text style={{ color: "#64748B", fontSize: 11, marginTop: 4 }}>
                        Only .XLSX file allowed
                      </Text>
                    </>
                  )}
                </TouchableOpacity>

                <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 32 }}>
                  <View style={{ flex: 1, height: 1, backgroundColor: "#E2E8F0" }} />
                  <Text style={{ marginHorizontal: 12, color: "#94A3B8", fontSize: 12, fontWeight: "600" }}>OR MANUAL ENTRY</Text>
                  <View style={{ flex: 1, height: 1, backgroundColor: "#E2E8F0" }} />
                </View>
              </>
            )}

            <Controller
              control={control}
              rules={{ required: false }}
              name="importer"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.importerList}
                  defaultOption={value}
                  title="Importer"
                  width="100%"
                  placeholder="Select Importer"
                  required={false}
                  onSelectedValue={onChange}
                  open={activePicker === "importer"}
                  setOpen={(val: any) => setActivePicker(val ? "importer" : null)}
                />
              )}
            />
            {errors.importer && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="type"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.modalityTypeList}
                  defaultOption={value}
                  title="Type"
                  width="100%"
                  placeholder="Select Type"
                  required={true}
                  onSelectedValue={onChange}
                  open={activePicker === "type"}
                  setOpen={(val: any) => setActivePicker(val ? "type" : null)}
                />
              )}
            />
            {errors.type && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="size"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.containerSizeList}
                  defaultOption={value}
                  title="Size"
                  width="100%"
                  placeholder="Select Type"
                  required={false}
                  onSelectedValue={onChange}
                  open={activePicker === "size"}
                  setOpen={(val: any) => setActivePicker(val ? "size" : null)}
                />
              )}
            />
            {errors.size && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{
                validate: {
                  required: async (value) => {
                    if (!value) return "* This is required.";
                    if (params.mode === "create") {
                      if (value && (await isContainerExists(value))) {
                        return "* Container Number already exists.";
                      }
                    }
                  },
                },
              }}
              name="container_number"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Container Number"
                  required={true}
                  width="100%"
                  placeholder="Enter Container Number"
                  value={value}
                  setChangeText={onChange}
                  editable={params.mode === "update" ? false : true}
                />
              )}
            />
            {errors.container_number && (
              <Text style={errors.container_number.message === "* Container Number is available." ? styles.successColor : styles.errorColor}>
                {errors.container_number.message}
              </Text>
            )}

            <Controller
              control={control}
              rules={{ required: true }}
              name="deliver_to"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Delivery Location"
                  required={true}
                  width="100%"
                  placeholder="Enter Delivery Location"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.deliver_to && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="country_state"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.countryStateList}
                  title="State"
                  width="100%"
                  placeholder="Select State"
                  required={true}
                  defaultOption={value}
                  onSelectedValue={onChange}
                  open={activePicker === "country_state"}
                  setOpen={(val: any) => setActivePicker(val ? "country_state" : null)}
                />
              )}
            />
            {errors.country_state && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="etd"
              render={({ field: { onChange, value } }) => (
                <DateInputControl
                  title="ETD"
                  required={true}
                  setDate={onChange}
                  value={value}
                  width="100%"
                  defaultText="Select ETD"
                  disabled={true}
                />
              )}
            />
            {errors.etd && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="eta_port"
              render={({ field: { onChange, value } }) => (
                <DateInputControl
                  title="ETA"
                  required={true}
                  setDate={onChange}
                  value={value}
                  width="100%"
                  defaultText="Select ETA"
                  disabled={true}
                />
              )}
            />
            {errors.eta_port && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="eta_store"
              render={({ field: { onChange, value } }) => (
                <DateInputControl
                  title="ETA to store"
                  required={true}
                  setDate={onChange}
                  value={value}
                  width="100%"
                  defaultText="Select ETA to store"
                  disabled={true}
                />
              )}
            />
            {errors.eta_store && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="updated_eta"
              render={({ field: { onChange, value } }) => (
                <DateInputControl
                  title="Updated ETA"
                  required={false}
                  setDate={onChange}
                  value={value}
                  width="100%"
                  defaultText="Select Updated ETA"
                  disabled={true}
                />
              )}
            />
            {errors.updated_eta && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="dd_number"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Packing List #"
                  required={false}
                  width="100%"
                  placeholder="Enter Packing List #"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.dd_number && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="coo_number"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Certificate of Origin (COO) #"
                  required={true}
                  width="100%"
                  placeholder="Enter COO #"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.coo_number && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="status"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.shipmentStatusList}
                  defaultOption={value}
                  title="Status"
                  width="100%"
                  placeholder="Select Status"
                  required={false}
                  onSelectedValue={onChange}
                  open={activePicker === "status"}
                  setOpen={(val: any) => setActivePicker(val ? "status" : null)}
                />
              )}
            />
            {errors.status && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="remark"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Remarks"
                  required={false}
                  width="100%"
                  placeholder="Enter Remarks"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.remark && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="delay_reason"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Delay Reason"
                  required={false}
                  width="100%"
                  placeholder="Enter Delay Reason"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.delay_reason && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: false }}
              name="vessal_detail"
              render={({ field: { onChange, value } }) => (
                <TextInputControl
                  title="Vessal Detail"
                  required={false}
                  width="100%"
                  placeholder="Enter Vessal Detail"
                  value={value}
                  setChangeText={onChange}
                />
              )}
            />
            {errors.vessal_detail && <Text style={styles.errorColor}>* This is required.</Text>}

            <Controller
              control={control}
              rules={{ required: true }}
              name="priority"
              render={({ field: { onChange, value } }) => (
                <SelectBoxControl
                  Items={containersState.priorityList}
                  defaultOption={value}
                  title="Priority"
                  width="100%"
                  placeholder="Select Priority"
                  required={true}
                  onSelectedValue={onChange}
                  open={activePicker === "priority"}
                  setOpen={(val: any) => setActivePicker(val ? "priority" : null)}
                />
              )}
            />
            {errors.priority && <Text style={styles.errorColor}>* This is required.</Text>}

            <View style={{ marginTop: 24 }}>
              {isUserAdmin && (
                <TouchableOpacity
                  onPress={handleSubmit(onSubmit)}
                  disabled={displayLoader}
                  style={styles.submitButton}
                >
                  {displayLoader ? (
                    <Spinner size="small" color="#fff" />
                  ) : (
                    <Text style={styles.submitButtonText}>
                      {params?.mode === "create" ? "Create Container" : "Save Changes"}
                    </Text>
                  )}
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  scrollContent: {
    paddingBottom: 48,
  },
  pageHeader: {
    paddingTop: 20,
    paddingBottom: 16,
    marginBottom: 24,
    paddingHorizontal: 24,
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    width: "100%",
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
  },
  pageSubtitle: {
    fontSize: 13,
    color: "#6B7280",
    marginTop: 2,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
    maxWidth: 800,
    width: "95%",
    alignSelf: "center",
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
    marginBottom: 6,
    marginTop: 14,
  },
  submitButton: {
    alignItems: "center",
    justifyContent: "center",
    height: 46,
    backgroundColor: "#2563EB",
    borderRadius: 10,
    marginTop: 24,
    width: "100%",
    shadowColor: "#2563EB",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 3,
  },
  submitButtonText: {
    color: "#ffffff",
    fontWeight: "700",
    fontSize: 15,
  },
  errorColor: {
    color: "#EF4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
  successColor: {
    color: "#10B981",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
  container: {
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "100%",
    backgroundColor: "#F9FAFB",
  },
});
